import React, { useState, useEffect } from "react";
import { Modal, Button, Table, Collapse, Card, Spinner } from "react-bootstrap";
import { api } from "../../services/api.js";
import {
  FaChevronDown,
  FaChevronUp,
  FaUndoAlt,
  FaCalendarAlt,
} from "react-icons/fa";

import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, LabelList, Legend
} from "recharts";

import Menu from "../../comum/Menu/menu.jsx";
import Footer from "../../comum/Footer/index.jsx";

// ================= FORMATADORES =================
const formatMoeda = (v) => v?.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const formatPercent = (v) => isNaN(v) || v === null ? "0,00%" : `${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
const formatLoja = (n) => String(n).padStart(2, '0');

const formatarMesExibicao = (mesAno) => {
  if (!mesAno || mesAno.length !== 4) return mesAno;
  return `${mesAno.slice(2)}/20${mesAno.slice(0, 2)}`;
};

const formatarParaInput = (mesAno) => {
  if (!mesAno || mesAno.length !== 4) return "";
  return `20${mesAno.slice(0, 2)}-${mesAno.slice(2)}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 border rounded shadow-sm" style={{ fontSize: '11px', zIndex: 3000 }}>
        <p className="fw-bold mb-1 border-bottom">{label}</p>
        {payload.map((p, i) => (
          <div key={i} style={{ color: p.color }} className="mb-1">
            <span className="fw-bold">{p.name}:</span> {formatPercent(p.value)}
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const VendaCompPeriodo = () => {
  const [mes1, setMes1] = useState(""); 
  const [mes2, setMes2] = useState(""); 
  const [dadosDepto, setDadosDepto] = useState([]);
  const [dadosSecao, setDadosSecao] = useState({});
  const [dadosLoja, setDadosLoja] = useState({});
  const [dadosGrafico, setDadosGrafico] = useState([]);
  const [tituloGrafico, setTituloGrafico] = useState("Departamentos");
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  
  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    const hoje = new Date();
    const mm = String(hoje.getMonth() + 1).padStart(2, "0");
    const aaAtual = String(hoje.getFullYear()).slice(2);
    const aaAnterior = String(hoje.getFullYear() - 1).slice(2);
    
    const pAnterior = `${aaAnterior}${mm}`;
    const pAtual = `${aaAtual}${mm}`;
    
    setMes1(pAnterior);
    setMes2(pAtual);
    carregarDepto(pAnterior, pAtual);
  }, []);

  const getDiasConsulta = (m1, m2) => {
    const mmAtual = String(new Date().getMonth() + 1).padStart(2, "0");
    const mmP1 = m1.slice(2);
    const mmP2 = m2.slice(2);

    if (mmP1 === mmAtual || mmP2 === mmAtual) {
      const ontem = new Date();
      ontem.setDate(ontem.getDate() - 1);
      const diaOntem = String(ontem.getDate()).padStart(2, "0");
      return { dia1: diaOntem, dia2: diaOntem };
    } else {
      const ultimoDia = (periodo) => {
        const ano = 2000 + parseInt(periodo.slice(0, 2));
        const mes = parseInt(periodo.slice(2));
        return String(new Date(ano, mes, 0).getDate()).padStart(2, "0");
      };
      return { dia1: ultimoDia(m1), dia2: ultimoDia(m2) };
    }
  };

  const mergeDados = (lista1, lista2, key, descKey) => {
    const allKeys = Array.from(new Set([...lista1.map(x => x[key]), ...lista2.map(x => x[key])]));
    return allKeys.map(k => {
      const d1 = lista1.find(x => x[key] === k) || { venda: 0, meta: 0, atingido: 0, margem: 0 };
      const d2 = lista2.find(x => x[key] === k) || { venda: 0, meta: 0, atingido: 0, margem: 0 };
      return { id: k, descricao: d2[descKey] || d1[descKey] || "N/D", p1: d1, p2: d2 };
    });
  };

  const carregarDepto = async (m1, m2) => {
    setLoading(true);
    setDadosSecao({});
    setDadosLoja({});
    const chapa = localStorage.getItem("chapa");
    const p1 = m1 || mes1;
    const p2 = m2 || mes2;
    const { dia1, dia2 } = getDiasConsulta(p1, p2);

    try {
      const [res1, res2] = await Promise.all([
        api.get(`/venda/mes/comp/depto?mes=${p1}&usuario=${chapa}&dia=${dia1}`),
        api.get(`/venda/mes/comp/depto?mes=${p2}&usuario=${chapa}&dia=${dia2}`)
      ]);
      const merged = mergeDados(res1.data || [], res2.data || [], "dep", "ddepartamento");
      setDadosDepto(merged);
      setDadosGrafico(merged.map(d => ({
        nome: d.descricao.split("-")[1] || d.descricao,
        atingidoP1: d.p1.atingido,
        atingidoP2: d.p2.atingido
      })));
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const buscarSecao = async (depCod, depNome) => {
    if (dadosSecao[depCod]) {
      const copy = { ...dadosSecao }; delete copy[depCod];
      setDadosSecao(copy);
      return;
    }
    setLoading(true);
    const chapa = localStorage.getItem("chapa");
    const { dia1, dia2 } = getDiasConsulta(mes1, mes2);
    try {
      const [res1, res2] = await Promise.all([
        api.get(`/venda/mes/comp/deptosecao?mes=${mes1}&usuario=${chapa}&depto=${depCod}&dia=${dia1}`),
        api.get(`/venda/mes/comp/deptosecao?mes=${mes2}&usuario=${chapa}&depto=${depCod}&dia=${dia2}`)
      ]);
      const merged = mergeDados(res1.data || [], res2.data || [], "codsecao", "descsecao");
      setDadosSecao({ ...dadosSecao, [depCod]: merged });
      setDadosGrafico(merged.map(s => ({ nome: s.descricao, atingidoP1: s.p1.atingido, atingidoP2: s.p2.atingido })));
      setTituloGrafico(`Seções: ${depNome}`);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const buscarLoja = async (depCod, secCod, secNome) => {
    const key = `${depCod}-${secCod}`;
    if (dadosLoja[key]) {
      const copy = { ...dadosLoja }; delete copy[key];
      setDadosLoja(copy);
      return;
    }
    setLoading(true);
    const chapa = localStorage.getItem("chapa");
    const { dia1, dia2 } = getDiasConsulta(mes1, mes2);
    try {
      const [res1, res2] = await Promise.all([
        api.get(`/venda/mes/comp/deptosecaoloja?mes=${mes1}&usuario=${chapa}&depto=${depCod}&secao=${secCod}&dia=${dia1}`),
        api.get(`/venda/mes/comp/deptosecaoloja?mes=${mes2}&usuario=${chapa}&depto=${depCod}&secao=${secCod}&dia=${dia2}`)
      ]);
      const merged = mergeDados(res1.data || [], res2.data || [], "filial", "filial");
      setDadosLoja({ ...dadosLoja, [key]: merged });
      setDadosGrafico(merged.map(l => ({ nome: `${formatLoja(l.id)}`, atingidoP1: l.p1.atingido, atingidoP2: l.p2.atingido })));
      setTituloGrafico(`Lojas: ${secNome}`);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const calcTotal = (periodo) => dadosDepto.reduce((acc, curr) => {
    acc.venda += curr[periodo].venda;
    acc.meta += curr[periodo].meta;
    return acc;
  }, { venda: 0, meta: 0 });

  const totalP1 = calcTotal("p1");
  const totalP2 = calcTotal("p2");

  // NOVO RENDER MOBILE LADO A LADO
  const renderCardComparison = (p1Data, p2Data) => (
    <div className="row g-1 mb-2">
      <div className="col-6">
        <div className="p-2 rounded bg-periodo1 border-end h-100">
          <div className="fw-bold text-muted x-small mb-1 border-bottom">{formatarMesExibicao(mes1)}</div>
          <div className="x-small">Venda: <b>{formatMoeda(p1Data.venda)}</b></div>
          <div className="x-small">Meta: <b>{formatMoeda(p1Data.meta)}</b></div>
          <div className="x-small">Mg: <b>{formatPercent(p1Data.margem)}</b></div>
          <div className="x-small fw-bold mt-1">
            At: <span className={p1Data.atingido >= 100 ? "text-success" : "text-danger"}>{formatPercent(p1Data.atingido)}</span>
          </div>
        </div>
      </div>
      <div className="col-6">
        <div className="p-2 rounded bg-white h-100 border">
          <div className="fw-bold text-primary x-small mb-1 border-bottom">{formatarMesExibicao(mes2)}</div>
          <div className="x-small">Venda: <b>{formatMoeda(p2Data.venda)}</b></div>
          <div className="x-small">Meta: <b>{formatMoeda(p2Data.meta)}</b></div>
          <div className="x-small">Mg: <b>{formatPercent(p2Data.margem)}</b></div>
          <div className="x-small fw-bold mt-1">
            At: <span className={p2Data.atingido >= 100 ? "text-success" : "text-danger"}>{formatPercent(p2Data.atingido)}</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="arbor-page">
      <Menu modulo="ARBOR" />
      
      {loading && <div className="loading-overlay"><Spinner animation="border" variant="primary" /></div>}

      <button className="btn-floating-calendar" onClick={() => setShowModal(true)}><FaCalendarAlt /></button>

      <div className="container-fluid content-wrapper p-2">
        <div className="d-flex justify-content-between align-items-center mb-3 mt-1 px-2">
          <h6 className="fw-bold m-0 text-muted">
            {formatarMesExibicao(mes1)} <span className="mx-1">x</span> <span className="text-primary">{formatarMesExibicao(mes2)}</span>
          </h6>
          <Button size="sm" variant="outline-primary" onClick={() => carregarDepto()} disabled={loading}><FaUndoAlt /> Reset</Button>
        </div>

        {/* GRÁFICO */}
        <div className="bg-white border rounded shadow-sm mb-4 p-2">
          <h6 className="text-center small text-muted mb-3">{tituloGrafico} (%)</h6>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={dadosGrafico} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="nome" tick={{fontSize: 9}} />
                <YAxis tick={{fontSize: 10}} />
                <Tooltip content={<CustomTooltip />} />
                <Legend iconSize={10} wrapperStyle={{fontSize: 10}} />
                <Bar name={formatarMesExibicao(mes1)} dataKey="atingidoP1" fill="#7fa9c3" radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="atingidoP1" position="top" formatter={(v) => `${v.toFixed(0)}%`} style={{fontSize: 8}} />
                </Bar>
                <Bar name={formatarMesExibicao(mes2)} dataKey="atingidoP2" fill="#007bff" radius={[4, 4, 0, 0]}>
                  <LabelList dataKey="atingidoP2" position="top" formatter={(v) => `${v.toFixed(0)}%`} style={{fontSize: 9, fontWeight: 'bold'}} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* TOTAL GERAL */}
        {dadosDepto.length > 0 && (
          <Card className="mb-3 border-0 shadow-sm bg-dark text-white p-2 mx-1">
            <div className="text-center x-small fw-bold opacity-75 mb-2 border-bottom border-secondary pb-1">TOTAL GERAL</div>
            <div className="row g-0">
              <div className="col-6 border-end border-secondary pe-2 text-center">
                <div className="x-small opacity-75">{formatarMesExibicao(mes1)}</div>
                <div className="x-small">V: {formatMoeda(totalP1.venda)}</div>
                <div className="x-small">AT: <b className="text-warning">{formatPercent(totalP1.meta > 0 ? (totalP1.venda/totalP1.meta)*100 : 0)}</b></div>
              </div>
              <div className="col-6 ps-2 text-center">
                <div className="x-small text-primary">{formatarMesExibicao(mes2)}</div>
                <div className="x-small">V: {formatMoeda(totalP2.venda)}</div>
                <div className="x-small">AT: <b className="text-success">{formatPercent(totalP2.meta > 0 ? (totalP2.venda/totalP2.meta)*100 : 0)}</b></div>
              </div>
            </div>
          </Card>
        )}

        {/* MOBILE */}
        {isMobile ? (
          <div className="mobile-list pb-5">
            {dadosDepto.map((d) => (
              <Card key={d.id} className="mb-3 border-0 shadow-sm">
                <Card.Header className="bg-white py-2 border-0" onClick={() => buscarSecao(d.id, d.descricao)} style={{cursor: 'pointer'}}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fw-bold text-dark" style={{fontSize: '0.85rem'}}>{d.descricao}</span>
                    {dadosSecao[d.id] ? <FaChevronUp /> : <FaChevronDown />}
                  </div>
                  {renderCardComparison(d.p1, d.p2)}
                </Card.Header>
                <Collapse in={!!dadosSecao[d.id]}>
                  <Card.Body className="bg-light pt-0 px-2 pb-2">
                    {dadosSecao[d.id]?.map((s) => (
                      <div key={s.id} className="bg-white rounded p-2 mb-2 border shadow-xs">
                        <div className="d-flex justify-content-between align-items-center fw-bold mb-2 border-bottom pb-1" 
                             onClick={() => buscarLoja(d.id, s.id, s.descricao)} style={{cursor: 'pointer'}}>
                          <span className="text-primary x-small">{s.descricao}</span>
                          {dadosLoja[`${d.id}-${s.id}`] ? <FaChevronUp size={12}/> : <FaChevronDown size={12}/>}
                        </div>
                        {renderCardComparison(s.p1, s.p2)}
                        <Collapse in={!!dadosLoja[`${d.id}-${s.id}`]}>
                          <div className="mt-2 border-top pt-2">
                            {dadosLoja[`${d.id}-${s.id}`]?.map((l, i) => (
                              <div key={i} className="mb-2 p-2 border-start border-primary border-3 bg-light shadow-xs rounded">
                                <div className="fw-bold mb-1 x-small text-uppercase">Loja {formatLoja(l.id)}</div>
                                {renderCardComparison(l.p1, l.p2)}
                              </div>
                            ))}
                          </div>
                        </Collapse>
                      </div>
                    ))}
                  </Card.Body>
                </Collapse>
              </Card>
            ))}
          </div>
        ) : (
          /* DESKTOP */
          <div className="table-responsive shadow-sm rounded">
            <Table size="sm" className="bg-white table-align-middle mb-0 border">
              <thead className="table-dark">
                <tr>
                  <th width="40"></th>
                  <th>Descrição</th>
                  <th className="text-center">Período</th>
                  <th className="text-end">Venda</th>
                  <th className="text-end">Meta</th>
                  <th className="text-end">% Ating.</th>
                  <th className="text-end">Margem</th>
                </tr>
              </thead>
              <tbody>
                {dadosDepto.map((d) => (
                  <React.Fragment key={d.id}>
                    <tr onClick={() => buscarSecao(d.id, d.descricao)} style={{ cursor: "pointer", borderTop: '2px solid #dee2e6' }} className="fw-bold">
                      <td rowSpan={2} className="text-center align-middle bg-white">
                        {dadosSecao[d.id] ? <FaChevronUp /> : <FaChevronDown />}
                      </td>
                      <td rowSpan={2} className="align-middle bg-white">{d.descricao}</td>
                      <td className="text-center small text-muted bg-periodo1">{formatarMesExibicao(mes1)}</td>
                      <td className="text-end text-muted bg-periodo1">{formatMoeda(d.p1.venda)}</td>
                      <td className="text-end text-muted bg-periodo1">{formatMoeda(d.p1.meta)}</td>
                      <td className="text-end text-muted bg-periodo1">{formatPercent(d.p1.atingido)}</td>
                      <td className="text-end text-muted bg-periodo1">{formatPercent(d.p1.margem)}</td>
                    </tr>
                    <tr onClick={() => buscarSecao(d.id, d.descricao)} style={{ cursor: "pointer" }} className="fw-bold">
                      <td className="text-center small text-primary bg-white">{formatarMesExibicao(mes2)}</td>
                      <td className="text-end text-primary bg-white">{formatMoeda(d.p2.venda)}</td>
                      <td className="text-end bg-white">{formatMoeda(d.p2.meta)}</td>
                      <td className={`text-end bg-white ${d.p2.atingido >= 100 ? "text-success" : "text-danger"}`}>{formatPercent(d.p2.atingido)}</td>
                      <td className="text-end bg-white">{formatPercent(d.p2.margem)}</td>
                    </tr>

                    {dadosSecao[d.id] && dadosSecao[d.id].map((s) => (
                      <React.Fragment key={s.id}>
                        <tr className="bg-light" onClick={() => buscarLoja(d.id, s.id, s.descricao)} style={{ cursor: "pointer" }}>
                          <td rowSpan={2} className="text-center align-middle">{dadosLoja[`${d.id}-${s.id}`] ? <FaChevronUp size={12}/> : <FaChevronDown size={12}/>}</td>
                          <td rowSpan={2} className="align-middle ps-4 text-primary">└ {s.descricao}</td>
                          <td className="text-center small bg-periodo1">{formatarMesExibicao(mes1)}</td>
                          <td className="text-end bg-periodo1">{formatMoeda(s.p1.venda)}</td>
                          <td className="text-end bg-periodo1">{formatMoeda(s.p1.meta)}</td>
                          <td className="text-end bg-periodo1">{formatPercent(s.p1.atingido)}</td>
                          <td className="text-end bg-periodo1">{formatPercent(s.p1.margem)}</td>
                        </tr>
                        <tr className="bg-light" onClick={() => buscarLoja(d.id, s.id, s.descricao)} style={{ cursor: "pointer" }}>
                          <td className="text-center small text-primary">{formatarMesExibicao(mes2)}</td>
                          <td className="text-end text-primary">{formatMoeda(s.p2.venda)}</td>
                          <td className="text-end">{formatMoeda(s.p2.meta)}</td>
                          <td className={`text-end ${s.p2.atingido >= 100 ? "text-success" : "text-danger"}`}>{formatPercent(s.p2.atingido)}</td>
                          <td className="text-end">{formatPercent(s.p2.margem)}</td>
                        </tr>

                        {dadosLoja[`${d.id}-${s.id}`] && dadosLoja[`${d.id}-${s.id}`].map((l) => (
                          <React.Fragment key={l.id}>
                            <tr style={{fontSize: '0.85rem'}}>
                              <td></td>
                              <td className="ps-5 text-muted small">Loja {formatLoja(l.id)}</td>
                              <td className="text-center bg-periodo1">{formatarMesExibicao(mes1)}</td>
                              <td className="text-end bg-periodo1">{formatMoeda(l.p1.venda)}</td>
                              <td className="text-end bg-periodo1">{formatMoeda(l.p1.meta)}</td>
                              <td className="text-end bg-periodo1">{formatPercent(l.p1.atingido)}</td>
                              <td className="text-end bg-periodo1">{formatPercent(l.p1.margem)}</td>
                            </tr>
                            <tr style={{fontSize: '0.85rem'}} className="border-bottom">
                              <td></td>
                              <td></td>
                              <td className="text-center text-primary">{formatarMesExibicao(mes2)}</td>
                              <td className="text-end text-primary">{formatMoeda(l.p2.venda)}</td>
                              <td className="text-end">{formatMoeda(l.p2.meta)}</td>
                              <td className={`text-end ${l.p2.atingido >= 100 ? "text-success" : "text-danger"}`}>{formatPercent(l.p2.atingido)}</td>
                              <td className="text-end">{formatPercent(l.p2.margem)}</td>
                            </tr>
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="sm" style={{zIndex: 3000}}>
        <Modal.Header closeButton><Modal.Title className="fs-6">Períodos</Modal.Title></Modal.Header>
        <Modal.Body>
          <label className="x-small fw-bold">Período 1</label>
          <input type="month" className="form-control mb-2" value={formatarParaInput(mes1)}
                 onChange={(e) => { const [a, m] = e.target.value.split("-"); setMes1(`${a.slice(2)}${m}`); }} />
          <label className="x-small fw-bold">Período 2</label>
          <input type="month" className="form-control mb-3" value={formatarParaInput(mes2)}
                 onChange={(e) => { const [a, m] = e.target.value.split("-"); setMes2(`${a.slice(2)}${m}`); }} />
          <Button variant="primary" className="w-100" onClick={() => { carregarDepto(); setShowModal(false); }}>Atualizar</Button>
        </Modal.Body>
      </Modal>

      <Footer />
      <style>{`
        .arbor-page { background-color: #f8f9fa; min-height: 100vh; }
        .content-wrapper { padding-top: 85px !important; } 
        .loading-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(255,255,255,0.7); display: flex; justify-content: center; align-items: center; z-index: 9999; }
        .btn-floating-calendar { position: fixed; bottom: 85px; right: 20px; width: 55px; height: 55px; border-radius: 50%; background: #28a745; color: white; border: none; z-index: 2000; box-shadow: 0 4px 12px rgba(0,0,0,0.3); display: flex; justify-content: center; align-items: center; font-size: 20px; }
        .x-small { font-size: 0.68rem; }
        .bg-periodo1 { background-color: #e9f2f9 !important; }
        .table-align-middle td { vertical-align: middle; }
        .mobile-list .card-header { padding: 10px 8px; }
      `}</style>
    </div>
  );
};

export default VendaCompPeriodo;